import React, { useMemo } from "react";
import { Grid2, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslate } from "../../../hooks/useTranslate";
import { useDetailsForm } from "../hooks/useDetailsForm";
import { useGetRelationTypes } from "../../../hooks/useGetRelationTypes";
import { useGetGenders } from "../../../hooks/useGetGenders";
import TextInput from "../../../components/TextInput";
import DatePickerInput from "../../../components/DatePickerInput";
import Select from "../../../components/Select";
import { useGetYearGroups } from "../../../hooks/useGetYearGroups";
import { useGetStartDates } from "../../../hooks/useGetStartDates";
import { useToast } from "../../../context/toast";
import axiosManagerRegister from "../../../services/axiosManagerRegister";
import useStatuses from "../../../context/status/useStatuses";
import { useUpdateChild } from "../hooks/useUpdateChild";
import { useGetCountries } from "../../../hooks/useGetCountries";
import { useGetStudentContacts } from "../../../hooks/useGetStudentContacts";
import { useStudents } from "../../../context/students";
import axiosManager from "../../../services/axiosManager";

const MAX_DATE_OF_BIRTH = `${(new Date().getFullYear() - 7)}-01-01`;

export const DetailsForm = (props) => {
    const {
        detailsData, onDetailsDataChange,
        registrationId, onRegistrationIdChange,
        onSessionKeyChange,
        onNext,
    } = props;
    const { t } = useTranslate();
    const toast = useToast();

    const { contact } = useStatuses();
    const { students } = useStudents();

    const { gendersOptions, isLoading: isLoadingGenders } = useGetGenders();
    const { countriesOptions, isLoading: isLoadingCountries } = useGetCountries();
    const { relationshipsOptions, isLoading: isLoadingRelations } = useGetRelationTypes();
    const { yearGroupsOptions, isLoading: isLoadingYearGroups } = useGetYearGroups();
    const { startDatesOptions, isLoading: isLoadingStartDates } = useGetStartDates();

    const { contacts, isLoading: isLoadingContacts } = useGetStudentContacts(students?.[0]?.studentId);
    const contactId = useMemo(() => contacts.find((contact) => contact.isPrimary)?.contactId, [contacts]);
    const addressFields = useMemo(() => contacts.find((contact) => contact.isPrimary)?.primaryAddress, [contacts]);
    const phoneFields = useMemo(() => contacts.find((contact) => contact.isPrimary)?.primaryPhone, [contacts]);

    const { data: user, isFetching: isLoadingUser } = useQuery({
        queryKey: ["parent/contacts"],
        queryFn: () => axiosManager.get$("parent/api/Contacts").then((response) => response?.data),
        initialData: {},
        retry: 0,
    });

    const {
        isFetching: isLoadingCustomerId, data: stripeCustomerId,
    } = useQuery({
        queryKey: [`add-student/contacts/${contactId}/payment/customerId`],
        queryFn: () => axiosManager.get$(`/parent/api/contacts/${contactId}/payment/customerId`).then((response) => response?.data?.stripeCustomerId),
        retry: 0,
        enabled: !!contactId,
    });
    const {
        isFetching: isLoadingOLRFParentId, data: OLRFParentId,
    } = useQuery({
        queryKey: [`add-student/contacts/${contactId}/OLRFParentId`],
        queryFn: () => axiosManager.get$(`/parent/api/contacts/${contactId}/OLRFParentId`).then((response) => response?.data?.olrfParentId),
        retry: 0,
        enabled: !!contactId,
    });

    const {
        register, control, errors, handleSubmit,
    } = useDetailsForm(detailsData);

    const {
        mutateAsync: createRegistration, isPending: isCreatingRegistration,
    } = useMutation({
        mutationFn: () => axiosManagerRegister.post$("/api/api/registration", null).then((response) => response?.data),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    const {
        mutateAsync: createLead, isPending: isCreatingLead,
    } = useMutation({
        mutationFn: ({ regId, requestData }) => axiosManagerRegister.put$(`/api/api/registration/${regId}/lead`, { ...requestData, onlineregistrationid: regId }).then((response) => response?.data),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    const { updateChild, isUpdatingChild } = useUpdateChild();

    const onSubmit = handleSubmit(async (formData) => {
        const studySchedule = "Full Time"; // TODO: Remove hardcode
        const childData = {
            childId: detailsData?.childId,
            forename: formData.forename,
            surname: formData.surname,
            dateOfBirth: formData.dateOfBirth,
            countryOfResidence: formData.nationality,
            currentNotionalYearGroup: formData.yearGroupId,
            desiredStartDate: formData.startDate,
            genderTypeId: formData.genderTypeId,
            studySchedule,
            relationshipTypeId: formData.relationshipTypeId,
        };

        const { addressId, verificationStatus, ...addressContact } = addressFields || {};
        const leadData = {
            forename: contact?.forename,
            surname: contact?.surname,
            suffix: contact?.suffix,
            title: contact?.title,
            emailAddress: contact?.emailAddress,
            leadId: detailsData?.leadId,
            relationshipTypeId: formData.relationshipTypeId,
            ...addressContact,
            phonenumber: phoneFields?.number,
            phonenumbercountrycode: phoneFields?.countryCode,
            iosContactId: user.contactId,
            stripeCustomerId: stripeCustomerId || undefined,
            olrfParentId: OLRFParentId || undefined,
        };

        onDetailsDataChange(childData);

        try {
            if (registrationId) {
                const resultLead = await createLead({
                    regId: registrationId,
                    requestData: { ...leadData, relationshipTypeId: formData.relationshipTypeId },
                });
                const resultChild = await updateChild({ regId: registrationId, leadId: resultLead.leadId, requestData: childData });

                onDetailsDataChange({ ...childData, childId: resultChild.childId, leadId: resultLead.leadId });
            } else {
                const result = await createRegistration();
                onRegistrationIdChange(result?.onlineRegistrationId);
                onSessionKeyChange(result?.sessionKey);
                axiosManagerRegister.setSessionKey(result?.sessionKey);

                const regId = result?.onlineRegistrationId;
                const resultLead = await createLead({
                    regId, requestData: { ...leadData, relationshipTypeId: formData.relationshipTypeId },
                });
                const resultChild = await updateChild({ regId, leadId: resultLead.leadId, requestData: childData });

                onDetailsDataChange({ ...childData, childId: resultChild.childId, leadId: resultLead.leadId });
            }

            onNext?.();
        } catch {
            // silently fails
        }
    });

    return (
        <div className="details-form">
            <Typography variant="h1">
                {t("addStudent.addNewStudent")}
            </Typography>
            <Typography variant="body1" component="div">
                {t("addStudent.addNewStudentSub")}
            </Typography>
            <form
                onSubmit={onSubmit}
                className="add-student__form-narrow mt-8"
            >
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextInput
                            label={t("addStudent.detailsForm.forename")}
                            placeholder={t("addStudent.detailsForm.forenamePlaceholder")}
                            register={register}
                            name="forename"
                            error={errors.forename?.message}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextInput
                            label={t("addStudent.detailsForm.surname")}
                            placeholder={t("addStudent.detailsForm.surnamePlaceholder")}
                            register={register}
                            name="surname"
                            error={errors.surname?.message}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <DatePickerInput
                            label={t("addStudent.detailsForm.dateOfBirth")}
                            control={control}
                            name="dateOfBirth"
                            maxDate={MAX_DATE_OF_BIRTH}
                            error={errors.dateOfBirth?.message}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Select
                            label={t("addStudent.detailsForm.gender")}
                            placeholder={t("addStudent.detailsForm.selectPlaceholder")}
                            control={control}
                            name="genderTypeId"
                            disabled={isLoadingGenders}
                            options={gendersOptions}
                            error={errors.genderTypeId?.message}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Select
                            label={t("addStudent.detailsForm.yearGroup")}
                            placeholder={t("addStudent.detailsForm.selectPlaceholder")}
                            control={control}
                            name="yearGroupId"
                            options={yearGroupsOptions}
                            disabled={isLoadingYearGroups}
                            error={errors.yearGroupId?.message}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Select
                            label={t("addStudent.detailsForm.residence")}
                            placeholder={t("addStudent.detailsForm.selectPlaceholder")}
                            control={control}
                            name="nationality"
                            options={countriesOptions}
                            disabled={isLoadingCountries}
                            error={errors.nationality?.message}
                            renderOption={(props, option) => (
                                <li {...props} key={option.key}>
                                    {option.name}
                                </li>
                            )}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Select
                            label={t("addStudent.detailsForm.startDate")}
                            placeholder={t("addStudent.detailsForm.selectPlaceholder")}
                            control={control}
                            name="startDate"
                            options={startDatesOptions}
                            disabled={isLoadingStartDates}
                            error={errors.startDate?.message}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Select
                            label={t("addStudent.detailsForm.relationshipType")}
                            placeholder={t("addStudent.detailsForm.selectPlaceholder")}
                            control={control}
                            name="relationshipTypeId"
                            disabled={isLoadingRelations}
                            options={relationshipsOptions}
                            error={errors.relationshipTypeId?.message}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            className="button-accent w-full mt-4 add-student-form__button"
                            loading={isCreatingLead || isCreatingRegistration || isUpdatingChild}
                            disabled={isLoadingContacts || isLoadingCustomerId || isLoadingOLRFParentId || isLoadingUser}
                        >
                            {t("button.next")}
                        </Button>
                    </Grid2>
                </Grid2>
            </form>
        </div>
    );
};
